import React, { useEffect, useRef, useState } from "react";
import "./ReactionButton.css";

// Like / Love button shared by the WTF media viewers. It renders the same button and calls the same
// handler as before; on top of that each click pops the icon and bursts a few mini icons around it.
// Purely visual: the like/love state still comes from the parent's counts. `pressed` (this user has
// already liked / loved the pin) keeps the icon filled, so it reads as done until it is clicked again.
const PARTICLE_ANGLES = [0, 60, 120, 180, 240, 300];
const BURST_MS = 750;

// Distances are percentages of the particle's own size (see ReactionButton.css).
const offset = (angle, distance) => {
  const rad = ((angle - 90) * Math.PI) / 180;
  return {
    "--dx": `${(Math.cos(rad) * distance).toFixed(1)}%`,
    "--dy": `${(Math.sin(rad) * distance).toFixed(1)}%`,
  };
};

const ReactionButton = ({
  variant = "like",
  icon,
  onClick,
  pressed = false,
  className = "",
  children,
  ...props
}) => {
  const [burst, setBurst] = useState(0);
  const [active, setActive] = useState(false);
  // Clicking an already pressed button takes the reaction back, so it skips the filled flash
  const [undoing, setUndoing] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const handleClick = (event) => {
    setBurst((count) => count + 1);
    setActive(true);
    setUndoing(pressed);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setActive(false), BURST_MS);
    if (onClick) onClick(event);
  };

  return (
    <button
      type="button"
      {...props}
      onClick={handleClick}
      aria-pressed={pressed}
      className={`wtf-reaction wtf-reaction--${variant}${pressed ? " wtf-reaction--pressed" : ""} ${className}`}
    >
      <span className="wtf-reaction__icon">
        {/* Re-keyed on every click so the pop animation restarts on rapid clicks */}
        <span
          key={burst}
          className={`wtf-reaction__glyph${active ? " wtf-reaction__glyph--pop" : ""}${
            active && undoing ? " wtf-reaction__glyph--undo" : ""
          }`}
        >
          {icon}
        </span>
        {active && (
          <span key={`burst-${burst}`} className="wtf-reaction__burst" aria-hidden="true">
            <span className="wtf-reaction__ring" />
            {PARTICLE_ANGLES.map((angle) => (
              <span key={angle} className="wtf-reaction__particle" style={offset(angle, 300)}>
                {icon}
              </span>
            ))}
            {PARTICLE_ANGLES.map((angle) => (
              <span key={`spark-${angle}`} className="wtf-reaction__spark" style={offset(angle + 30, 620)} />
            ))}
          </span>
        )}
      </span>
      {children}
    </button>
  );
};

export default ReactionButton;
